import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import RulesContext from "../../../../context/rules-context";
import { basicBoxesList, categoryColors, comparisionOptions } from "../Boxes/utils";

const boxCatalogByType = {};
basicBoxesList.forEach((box) => {
    boxCatalogByType[box.type] = box;
});

export function getBoxCatalogEntry(type) {
    return boxCatalogByType[type] || null;
}

export function collectUnknownBoxTypes(nodes) {
    let unknownTypes = [];
    (nodes || []).forEach((node) => {
        if (!getBoxCatalogEntry(node.type) && !unknownTypes.includes(node.type)) {
            unknownTypes.push(node.type);
        }
        collectUnknownBoxTypes(node.children).forEach((childType) => {
            if (!unknownTypes.includes(childType)) unknownTypes.push(childType);
        });
    });
    return unknownTypes;
}

export function collectUnavailableBoxTypes(nodes, companyBoxes) {
    if (!companyBoxes) return [];
    let unavailableTypes = [];
    let visit = (list) => {
        (list || []).forEach((node) => {
            if (
                getBoxCatalogEntry(node.type) &&
                companyBoxes[node.type] == null &&
                !unavailableTypes.includes(node.type)
            ) {
                unavailableTypes.push(node.type);
            }
            visit(node.children);
        });
    };
    visit(nodes);
    return unavailableTypes;
}

export function countTreeNodes(nodes) {
    let count = 0;
    (nodes || []).forEach((node) => {
        count += 1 + countTreeNodes(node.children);
    });
    return count;
}

// Box settings that map an enum stored in properties.value to the option
// description the rule tree shows for it.
const valueOptionsSettingByType = {
    action_decision_box: "decision_options",
    action_risk_level_box: "risk_level_options"
};

function describeValue(value, t) {
    if (typeof value === "boolean") return value ? t("Verdadeiro") : t("Falso");
    return String(value);
}

function describeFieldPath(fieldPath, rulesContext, t) {
    let field = (rulesContext.fields_mapper_list || []).find((mapping) => mapping.path === fieldPath);
    return field && field.description ? t(field.description) : null;
}

function describeComparison(properties, t) {
    let comparison = comparisionOptions.find((option) => option.value === properties.comparision_type);
    if (!comparison) return null;
    if (comparison.value === "in_between") {
        return t("Está") + " " + t(comparison.description).toLowerCase() + " " +
            describeValue(properties.min_value, t) + " " + t("e") + " " + describeValue(properties.max_value, t);
    }
    if (!hasValue(properties.value)) return t(comparison.description);
    return t(comparison.description) + " " + describeValue(properties.value, t);
}

function describeEnumValue(node, rulesContext, t) {
    let value = node.properties.value;
    if (node.type === "onboarding_enrichment_box") return t("onboarding_enrichment_box_" + value);
    let settingKey = valueOptionsSettingByType[node.type];
    if (!settingKey) return undefined;
    let box = (rulesContext.boxes || {})[node.type];
    let options = (box && box.settings && box.settings[settingKey]) || [];
    let option = options.find((candidate) => candidate.value === value);
    return option && option.description ? t(option.description) : null;
}

function hasValue(value) {
    return value !== undefined && value !== null && value !== "";
}

// Mirrors what each box shows on the rule tree: technical identifiers
// (field paths, enum values) are resolved to their descriptions and omitted
// when no description is known, so they never leak into the card.
function summarizeNodeProperties(node, rulesContext, t) {
    let properties = node.properties || {};
    if (node.type === "label_box") return properties.label || null;
    if (properties.field_path) {
        let fieldName = describeFieldPath(properties.field_path, rulesContext, t);
        let comparison = describeComparison(properties, t);
        if (fieldName && comparison) return fieldName + " · " + comparison;
        return fieldName || comparison;
    }
    if (properties.variable_name) return properties.variable_name;
    if (hasValue(properties.value)) {
        let enumDescription = describeEnumValue(node, rulesContext, t);
        if (enumDescription !== undefined) return enumDescription;
        return describeValue(properties.value, t);
    }
    return null;
}

function MiniRuleTreeNode({ node, depth, rulesContext, t }) {
    let catalogEntry = getBoxCatalogEntry(node.type);
    let summary = summarizeNodeProperties(node, rulesContext, t);

    return (
        <div className="qia-mini-node-block">
            <div className="qia-mini-node-row" style={{ marginLeft: depth * 14 + "px" }}>
                {catalogEntry ? (
                    <span
                        className="qia-mini-node-icon"
                        style={{ backgroundColor: categoryColors(catalogEntry.category) }}
                    >
                        <img src={catalogEntry.logoSVG} alt="" />
                    </span>
                ) : (
                    <span className="qia-mini-node-icon qia-mini-node-icon-unknown">?</span>
                )}
                <span className="qia-mini-node-name">
                    {catalogEntry ? t(catalogEntry.description) : node.type}
                </span>
                {summary && <span className="qia-mini-node-summary">{summary}</span>}
            </div>
            {(node.children || []).map((child, index) => (
                <MiniRuleTreeNode key={index} node={child} depth={depth + 1} rulesContext={rulesContext} t={t} />
            ))}
        </div>
    );
}

// Compact, read-only rendering of a rule fragment in the persisted
// (outcome) node shape: {type, properties, children, metadata}.
function MiniRuleTree({ treeData }) {
    const { t } = useTranslation();
    const rulesContext = useContext(RulesContext) || {};
    return (
        <div className="qia-mini-tree">
            {(treeData || []).map((node, index) => (
                <MiniRuleTreeNode key={index} node={node} depth={0} rulesContext={rulesContext} t={t} />
            ))}
        </div>
    );
}

export default MiniRuleTree;
